import { Handle, Position } from '@xyflow/react';
import { useContext } from 'react';
import { useParams } from 'react-router';
import InfoIcon from '@mui/icons-material/Info';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { CalendarIcon, FlashIconFlow, PlayIconFlow, Trash } from '../../../../assets/images';
import { MakeToast } from '../../../../components';
import StyledTooltip from '../../../../components/tooltip';
import { DisableWorkflowContext } from '../../../../utils/context';
import { deleteText } from '../data/constants';
import { runWorkFlowManually } from '../services';
import { customEdgeProps, customNodeProps } from '../automator.props';

export function CustomNode({
  data,
  id,
  isActionsOpen,
  toggleActions,
  nodeRef,
  handleDeleteNode,
  nodes,
}: customNodeProps) {
  const { disableMode } = useContext(DisableWorkflowContext);
  const { workflowId } = useParams();

  const runTrigger = () => {
    if (disableMode) {
      return;
    }
    const wfId =
      workflowId || JSON.parse(localStorage.getItem('workFlowData') || '{}')?.trigger?.workflow_id;
    runWorkFlowManually({ workflow_id: wfId })
      .then((res) => {
        MakeToast('success', res?.data?.message);
      })
      .catch((err) => {
        MakeToast('error', err?.response?.data?.message);
      });
  };

  return (
    <div
      ref={nodeRef}
      className="custom-node w-[300px] h-[100px] flex flex-col"
      style={{
        border: data?.selected ? '1px dashed #0828B7' : '1px dashed lightgray',
      }}
    >
      <div className="node-header pb-2">
        <img
          src={id === '1' ? FlashIconFlow : PlayIconFlow}
          alt="status icon"
          className="node-icon w-8 h-8"
        />
        {data?.trigger_type ? (
          <>
            <div className="trigger-title">
              <img src={CalendarIcon} alt="trigger icon" />
              <span className="node-title">{data.trigger_type}</span>
            </div>
            {data.trigger_type === 'Schedule' && (
              <StyledTooltip placement="top" title="Run trigger">
                <button
                  type="button"
                  className="ml-auto cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering the parent node click event

                    runTrigger(); // Run the trigger function
                  }}
                >
                  <PlayCircleOutlineIcon sx={{ color: 'blue' }} />
                </button>
              </StyledTooltip>
            )}
          </>
        ) : (
          <span className="node-title">{data?.title}</span>
        )}
        {(nodes?.length ?? 0) - 1 > 2 && id !== 1 && (
          <button
            type="button"
            className="node-options"
            onClick={(e) => {
              e.stopPropagation();
              toggleActions?.(id, true);
            }}
          >
            ⋮
          </button>
        )}
        {isActionsOpen && (
          <div className="absolute -right-40 top-8 w-44 bg-white border rounded-lg shadow-lg z-[10]">
            <button
              type="button"
              className="flex items-center w-full pl-2 py-2 gap-2 border-b-2"
              onClick={(e) => handleDeleteNode?.(e, id as string)}
            >
              <img src={Trash} height={15} width={15} alt="delete" />
              <span className="text-sm">{deleteText}</span>
            </button>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between h-full">
        <p className="font-extra-light">{data?.description}</p>
        {!data?.trigger_type && (
          <InfoIcon className="absolute right-[5px] z-[2]" sx={{ color: 'orange' }} />
        )}
      </div>
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

export function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  source,
  target,
  onAddNode,
}: customEdgeProps) {
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;
  const isBetweenLastAndEnd = target === 'end';
  return (
    <>
      {/* Draw the edge path */}
      <path
        id={id}
        className="react-flow__edge-path h-full"
        d={`M${sourceX},${sourceY} C${sourceX},${midY} ${targetX},${midY} ${targetX},${targetY}`}
      />
      {/* "+" Button in the middle */}
      {isBetweenLastAndEnd && (
        <foreignObject x={midX - 15} y={midY - 15} width={30} height={30}>
          <button
            type="button"
            className="add-node-btn"
            onClick={() => onAddNode?.(source, target)}
          >
            +
          </button>
        </foreignObject>
      )}
    </>
  );
}

export function CustomEnd({ data }: { data: { title: string } }) {
  return (
    <div className="custom-end-node">
      <span>{data.title}</span>
      <Handle type="target" position={Position.Top} />
    </div>
  );
}
